import { BoxProps, Container, Stack } from '@mantine/core';
import { useIsMdBreakpoint } from 'hooks/useIsMdBreakpoint';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Layout } from './Layout';
import { Form, FormError } from '../../../components/shared';
import { Page } from './Page';
import { Logo } from './Logo';
import { Header } from './Header';
import { AttachmentInfo } from './AttachemtnInfo';
import { FormStepper } from './FormStepper';
import { AttachmentsStep } from './AttachmentsStep';
import { DocumentStep } from './DocumentStep';
import { FinalStep } from './FinalStep';
import { OtpStep } from './OtpStep';
import { Footer } from './Footer';
import { StepsName } from '../types';
import {
  getSchema,
  getTemplateVariablesValues,
} from '../../../legacy/TemplatePreview/helpers';
import { getAttachmentsSchema } from '../helpers';

export type ExtFormProps = BoxProps & {
  formSlug: string;
  data: any;
  active: number;
  steps: StepsName[];
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  sendForm: (formData: any) => void;
  currentStepsName: string;
  disableRestoreData?: boolean;
};

/**
 * External form is a component that renders the external form
 * This component is without the form logic
 */
export const ExtForm: React.FC<ExtFormProps> = ({
  formSlug,
  data,
  active,
  steps,
  setStep,
  nextStep,
  prevStep,
  sendForm,
  currentStepsName,
  disableRestoreData,
  ...boxProps
}) => {
  const isMdBreakpoint = useIsMdBreakpoint();

  const contractTemplate = data?.template.contractTemplate;

  const defaultValues = contractTemplate
    ? {
        contractValues: { ...getTemplateVariablesValues(contractTemplate) },
      }
    : {};

  const validationSchema = contractTemplate
    ? yupResolver(
        yup.object({
          contractValues: getSchema(contractTemplate),
          fileAttachments: getAttachmentsSchema(data.attachments || []),
        }),
      )
    : undefined;

  return (
    <Layout displayBar={isMdBreakpoint} {...boxProps}>
      <Form
        cacheKey={disableRestoreData ? undefined : `ext-form-${formSlug}`}
        onSubmit={(formData: any) => sendForm(formData)}
        mode="onChange"
        defaultValues={defaultValues}
        resolver={validationSchema}
        cacheExclude={['code', 'otpId', 'fileAttachments']}
        style={{
          maxWidth: '100%',
        }}
      >
        <Page pt={36} pb={32} bg={isMdBreakpoint ? 'white' : 'transparent'}>
          <Stack
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Logo organisation={data?.organisation} />
            {currentStepsName !== 'submitted' && <Header />}
          </Stack>
        </Page>

        {data && currentStepsName === 'form' && !!data.attachments?.length && (
          <Page py={16}>
            <AttachmentInfo attachments={data.attachments} />
          </Page>
        )}

        <Page>
          <FormError />
        </Page>

        {currentStepsName !== 'submitted' && (
          <Page py={24} sx={{ position: 'sticky', top: 0, zIndex: 2 }}>
            <FormStepper
              small={!isMdBreakpoint}
              steps={steps}
              active={active}
              setStep={setStep}
            />
          </Page>
        )}

        {data && currentStepsName === 'form' && (
          <DocumentStep onSubmit={nextStep} template={data.template} />
        )}

        {active !== 0 && (
          <>
            {data && currentStepsName === 'attachments' && (
              <AttachmentsStep
                filesDescription={data.attachments || []}
                prevStep={prevStep}
                nextStep={nextStep}
              />
            )}

            {data && currentStepsName === 'otp' && (
              <OtpStep
                offerShareId={formSlug}
                prevStep={prevStep}
                signMethod={data.signType}
              />
            )}

            {data && currentStepsName === 'submitted' && <FinalStep />}
          </>
        )}

        <Container w="100%" size="xl" my={24}>
          <Footer />
        </Container>
      </Form>
    </Layout>
  );
};
